import { useField } from "formik";
import React from "react";
import {
  isDecimalNumberFormat,
  usesNumberCommaSeparators,
} from "src/constants/numberFormat";
import { NumberTypeConstraints } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import { formatNumberWithConstraints } from "src/services/format";
import { validateNumber } from "src/services/formTemplate/question";
import { isNotNull } from "src/services/predicates";
import { NumberInput } from "../NumberInput";
import { BaseInputProps } from "./Question";

export interface NumberProps extends BaseInputProps {
  constraints?: NumberTypeConstraints;
}

export const MaskedNumberInput: React.FC<NumberProps> = ({
  id,
  isDisabled = false,
  constraints,
}) => {
  const [field, , helpers] = useField({
    name: id,
    validate: (value: number) => validateNumber(value, constraints),
  });
  const handleChange = (value: string) => {
    const withCommas = usesNumberCommaSeparators(constraints?.format);
    if (!isDecimalNumberFormat(constraints?.format)) {
      const cleanValue = value.replace(/[^0-9]/g, "");
      value = withCommas
        ? cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : cleanValue;
    } else {
      // Remove any non-digit characters except for '.' (for decimals)
      value = value.replace(/[^0-9.]/g, "");
      // Split the value into integer and decimal parts
      const parts = value.split(".");

      if (parts[0] && withCommas) {
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }

      // If there is a decimal part, limit it to constraint.precision number of digits
      if (parts[1]) {
        parts[1] = parts[1].substring(0, constraints?.precision ?? 2);
      }

      // Join the integer and decimal parts back together
      value = parts.join(".");
    }

    helpers.setTouched(true);
    helpers.setValue(value);
  };

  const handleOnBlur = () => {
    helpers.setTouched(true);

    const precision = Number(constraints?.precision ?? 0);

    if (field.value?.includes(".")) {
      const parts = field.value.split(".");
      if (parts[1].length < precision) {
        const paddedDecimal =
          precision > 0 ? parts[1].padEnd(precision, "0") : "";
        helpers.setValue(`${parts[0]}.${paddedDecimal}`);
      }
      if (parts[1].length > precision) {
        if (precision === 0) {
          helpers.setValue(parts[0]);
        } else {
          helpers.setValue(`${parts[0]}.${parts[1].substring(0, precision)}`);
        }
      }
    }
  };

  React.useEffect(() => {
    helpers.setValue(formatNumberWithConstraints(field.value, constraints));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NumberInput
      id={id}
      name={id}
      onChangeEvent={handleChange}
      label=""
      value={field.value}
      isDisabled={isDisabled}
      minValue={
        isNotNull(constraints?.minValue)
          ? Number(constraints.minValue)
          : undefined
      }
      maxValue={
        isNotNull(constraints?.maxValue)
          ? Number(constraints.maxValue)
          : undefined
      }
      onKeyDown={(event) => {
        if (event.key === "." && field.value.includes(".")) {
          event.preventDefault();
        }
      }}
      onBlur={handleOnBlur}
    />
  );
};
